import React, {ReactElement, useState} from 'react';
import {Card, CardContent, Typography, makeStyles} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {useSnackbar} from 'notistack';
import {
  APP,
  DEFAULT_WHITE_COLOR,
  DEFAULT_BLACK_COLOR
} from 'src/style/variable';
import HelperUtils, {getNotificationTypeLabel} from '@shared/utils/HelperUtils';
import {AppPermissions, AppSmsStatus} from '@shared/constants/Mapping';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import './NotificationHistoryCard.scss';
import MinGridTable, {
  ColumnDef
} from '@components/system/MinGridTable/MinGridTable';
import WithPermission from '@components/system/WithPermission/WithPermission';
import PhoneNumberFormatter from '@components/system/PhoneNumberFormatter/PhoneNumberFormatter';
import {fetchNotificationHistory} from '@shared/services/NotificationServices';
import moment from 'moment'

const NotificationHistoryCardStyles = makeStyles(() => ({
  DetailsView: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: '1rem',
    rowGap: '1rem',
    overflowY: 'auto',
    maxHeight: '15.5rem'
  },
  notificationHistoryMainView: {
    flexDirection: 'row',
    display: 'flex',
    paddingLeft: '0.5rem'
  },
  notificationHistoryMainViews: {
    flexDirection: 'row',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '0.5rem',
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem'
  },
  notificationHistoryHeaderValue: {
    color: APP.STATUS.DEFAULT,
    fontSize: '1rem',
    padding: '1rem'
    // paddingLeft: '0.5rem'
  },
  textOverFlow: {
    fontSize: '1rem',
    textOverflow: 'ellipsis',
    overflowWrap: 'break-word',
    paddingRight: '0.5rem'
  },
  applyOddColor: {
    backgroundColor: 'rgb(0,0,0,0.05)'
  },
  applyEvenColor: {
    backgroundColor: DEFAULT_WHITE_COLOR
  },
  noNotificationHistoryInfoView: {
    flexDirection: 'row',
    display: 'flex'
  },
  divider: {
    marginTop: '0.5rem',
    marginBottom: '0.5rem'
  },
  tagsGuarantor: {
    // whiteSpace: 'nowrap',
    color: DEFAULT_WHITE_COLOR,
    borderRadius: '4px',
    padding: '4px',
    // marginLeft: '8px',
    fontSize: '0.8125rem',
    textAlign: 'center'
  },
  notification_delivery_status_sent: {
    backgroundColor: APP.COLOR.NOTIFICATION_DELIVERY_STATUS.SENT
  },
  notification_delivery_status_failed: {
    backgroundColor: APP.COLOR.NOTIFICATION_DELIVERY_STATUS.FAILED
  },
  notification_delivery_status_pending: {
    backgroundColor: APP.COLOR.NOTIFICATION_DELIVERY_STATUS.PENDING
  },
  notification_delivery_status_ignored: {
    backgroundColor: APP.COLOR.NOTIFICATION_DELIVERY_STATUS.IGNORED
  },

  notification_delivery_status_unknown: {
    color: DEFAULT_BLACK_COLOR
  },
  sectionWidth: {
    width: '30%'
  },
  typeWidth: {
    width: '25%',
    color: '#343434'
  },
  dateWidth: {
    width: '20%',
    color: '#343434'
  },
  reasonWidth: {
    width: '25%',
    color: '#8C8C8C'
  },
  displayGrid: {
    display: 'grid'
  },
  statusWidth: {
    width: '50%'
  },
  lineHeight: {
    lineHeight: '1rem'
  },
  overflow: {
    overflow: 'hidden'
  },
  cardRoot: {
    '& .MuiCardContent-root:last-child': {
      paddingBottom: '13px'
    }
  },
  setPadding: {
    paddingLeft: '0.5em'
  },
  fullPadding: {
    padding: '0.7rem'
  },
  sectionRow: {
    display: 'grid',
    gridTemplateColumns: '100%',
    fontSize: '1.3rem',
    paddingBlock: '1rem'
  },
  DrawerInfo: {
    fontSize: '1.3rem',
    fontWeight: 600
  },
  amountWrapper: {
    display: 'flex',
    justifyContent: 'flex-start',
    paddingLeft: '0.5rem'
  },
  cardHeader: {
    '& .MuiCardContent-root': {
      padding: '0'
    }
  },
  loaderStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
}));

interface NotificationHistoryCardProps {
  guarantorId: number | string;
  clinicId?: number;
}

const NotificationHistoryCard = (
  props: NotificationHistoryCardProps
): ReactElement => {
  const classes = NotificationHistoryCardStyles();
  const {guarantorId, clinicId} = props;
  const [notification, setNotificationHistory]: any = useState([]);

  const {t} = useTranslation();
  // FIXME: ADD PROPER TYPES
  const groupId: any = useSelector((state: any) => state.auth.groupId);

  const selectedClinicId = useSelector(
    (state: any) => state.clinics.selectedClinicId
  );

  const {enqueueSnackbar} = useSnackbar();
  const [
    isLoadingNotificationHistory,
    setIsLoadingNotificationHistory
  ] = useState<boolean>(true);
  const getNotificationHistory = async (): Promise<void> => {
    try {
      const options = {
        guarantor_id: guarantorId
      };
      const notificationHistoryResponse = await fetchNotificationHistory({
        clinic_group_id: groupId,
        clinic_id: (selectedClinicId && selectedClinicId[0]) ?? clinicId,
        params: {
          ...options,
          type: 'statement'
        }
      });
      if (notificationHistoryResponse?.data?.rows?.length > 0) {
        setNotificationHistory(notificationHistoryResponse?.data?.rows);
      }
    } catch (error: any) {
      enqueueSnackbar(error && error.message, SnackBarConfig('e'));
    } finally {
      setIsLoadingNotificationHistory(false);
    }
  };
  React.useEffect(() => {
    if (guarantorId) {
      getNotificationHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId]);

  const getNotificationStatus = (status: string): string => {
    let label: string;
    switch (status) {
      case AppSmsStatus.PENDING:
      case AppSmsStatus.QUEUED:
        label = 'SMS_STATUSES.PENDING';
        break;
      case AppSmsStatus.SENT:
        label = 'SMS_STATUSES.SENT';
        break;
      case AppSmsStatus.FAILED:
        label = 'SMS_STATUSES.FAILED';
        break;
      case AppSmsStatus.UNKNOWN:
        label = 'SMS_STATUSES.UNKNOWN';
        break;
      case AppSmsStatus.IGNORED:
        label = 'SMS_STATUSES.IGNORED';
        break;
      default:
        label = 'SMS_STATUSES.UNKNOWN';
    }
    return label;
  };

  const renderNotificationStatusColumn = (
    data: any,
    key: string
  ): ReactElement => {
    return (
      <div>
        {data[key] === 'pending' ? (
          <div
            className={[
              classes.tagsGuarantor,
              data[key] === 'pending'
                ? classes.notification_delivery_status_pending
                : ''
            ].join(' ')}>
            {t(getNotificationStatus(data[key]))}
          </div>
        ) : data[key] === 'sent' ? (
          <div
            className={[
              classes.tagsGuarantor,
              data[key] === 'sent'
                ? classes.notification_delivery_status_sent
                : ''
            ].join(' ')}>
            {t(getNotificationStatus(data[key]))}
          </div>
        ) : data[key] === 'failed' ? (
          <div
            className={[
              classes.tagsGuarantor,
              data[key] === 'failed'
                ? classes.notification_delivery_status_failed
                : ''
            ].join(' ')}>
            {t(getNotificationStatus(data[key]))}
          </div>
        ) : data[key] === 'ignored' ? ( // ADDED TO SHOW IGNORED NOTIFICATIONS
          <div
            className={[
              classes.tagsGuarantor,
              data[key] === 'ignored'
                ? classes.notification_delivery_status_ignored
                : ''
            ].join(' ')}>
            {t(getNotificationStatus(data[key]))}
          </div>
        ) : (
          <div
            className={[
              classes.tagsGuarantor,
              data[key] === 'queued'
                ? classes.notification_delivery_status_pending
                : classes.notification_delivery_status_unknown // TEXT COLOR BLACK TO UNKNOWN STATUS NOTIFICATIONS
            ].join(' ')}>
            {t(getNotificationStatus(data[key]))}
          </div>
        )}
      </div>
    );
  };

  const notificationColDef: ColumnDef<any>[] = [
    {
      label: t('PATIENT_DETAILS_CARD.ACTIVITY_TYPE'),
      render(notificationItem) {
        const notificationType = notificationItem.notification_type
          ? HelperUtils.captilizeFirstLetter(
              t(getNotificationTypeLabel(notificationItem?.notification_type))
            )
          : '—';
        return (
          <div>
            {notificationType}
            <div>
              {notificationItem.notification_type === 'email' ? (
                notificationItem?.email
              ) : notificationItem.notification_type === 'sms' ? (
                <PhoneNumberFormatter value={notificationItem?.phone || ''} />
              ) : (
                '—'
              )}
            </div>
          </div>
        );
      }
    },
    {
      label: t('PATIENT_DETAILS_CARD.DATE'),
      render(item) {
        if (!item.notification_batch_id) {
          return '-';
        }
        const parsedDate = moment(item.notification_batch_id, 'MMM-DD-YYYY-hh-mm-ss-A')
        return (
          <div>
            <span key={'date'}>
              {HelperUtils.formatDisplayDate(parsedDate)}
            </span>{' '}
            <span key={'time'} className={classes.overflow}>
              {HelperUtils.formatDisplayDateTime(
                parsedDate,
                'HH:mm:ss'
              )}
            </span>
          </div>
        );
      }
    },
    {
      label: t('PATIENT_DETAILS_CARD.STATUS'),
      render(item) {
        return renderNotificationStatusColumn(item, 'status');
      }
    },
    {
      label: t('PATIENT_DETAILS_CARD.FAILURE_REASON'),
      render(row) {
        return row.failed_reason || '—';
      }
    }
  ];

  return (
    <>
      <Typography
        data-test-id="pcl-payments-notificationHistoryInDrawer"
        className={[classes.sectionRow, classes.DrawerInfo].join(' ')}>
        {t('PATIENT_DETAILS_CARD.NOTIFICATION_HISTORY')}
      </Typography>
      <Card className={classes.cardHeader}>
        <CardContent>
          <MinGridTable
            isLoading={isLoadingNotificationHistory}
            noDataText={t('PATIENT_DETAILS_CARD.NO_NOTIFICATION_HISTORY')}
            colDefs={notificationColDef}
            rows={notification}
          />
        </CardContent>
      </Card>
    </>
  );
};
export default WithPermission(
  NotificationHistoryCard,
  AppPermissions.NOTIFICATION_HISTORY.NAME,
  AppPermissions.NOTIFICATION_HISTORY.LIST
);
